/**
 * Highlights the words and phrases the user is already learning ("yourWord")
 * in the reader text. Highlight state is a position -> phrases map in
 * ReaderTextStore; the text renderer derives classes from it, so adding or
 * removing a highlight is a signal write rather than DOM mutation.
 */
import $ from "jquery";
import * as _ from "underscore";

import * as analytics from "analytics";
import * as premiumUpsellModal from "premiumUpsellModal";
import type { ParsedReaderText, ReaderTextItem } from "readerText/tokens";
import * as languageMap from "shared/languageMap";
import type ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import * as tts from "tts";
import type { WordPopUpOptions } from "views/WordPopUpView";

type PhraseIndex = {
  [firstToken: string]: { whole: string; split: string[] }[];
};

type TokenIndex = {
  // A Map, not an object: word texts like "constructor" must not collide
  // with Object.prototype properties.
  textToPosition: Map<string, number[]>;
  positionToText: { [position: number]: string };
  /**
   * Word positions `p` such that the words at `p` and `p + 1` are directly
   * adjacent in the text: consecutive leaf items within the same block, with
   * no text run, `<br>`, or other item between them.
   */
  adjacentPositions: Set<number>;
  /**
   * True for languages without spaces between words (Chinese, Japanese, …),
   * where saved words are split into per-character tokens. Multi-token
   * phrases then only match runs of directly adjacent characters, so words
   * aren't stitched together across punctuation, whitespace, or paragraph
   * breaks — spannify assigns those no positions, which makes e.g. 你 and 好
   * in 你。好 numerically consecutive. Spaced languages keep matching on
   * consecutive positions alone, since their phrase words are legitimately
   * separated by whitespace (and word-internal punctuation like hyphens).
   */
  requireAdjacency: boolean;
};

const emptyTokenIndex = (): TokenIndex => ({
  textToPosition: new Map(),
  positionToText: {},
  adjacentPositions: new Set(),
  requireAdjacency: false,
});

let highlightPhrasesInitialized = false;
let tokenIndex = emptyTokenIndex();
let yourPhrasesByPosition = new Map<number, string[]>();
let shownUpsell = false;

// These will be injected from readerPage
let readerTextStore: ReaderTextStore;
let readerViewStore: ReaderViewStore;
let updateDictionary: any;
let setPreviousNewContext: any;
let readAloudButton: any;
let showWordPopUp: (options: WordPopUpOptions) => void;
let isSyncAudioPlaying: () => boolean;

export const initializePhraseHighlighter = (dependencies: {
  readerTextStore: ReaderTextStore;
  readerViewStore: ReaderViewStore;
  updateDictionary: any;
  setPreviousNewContext: any;
  readAloudButton: any;
  showWordPopUp: (options: WordPopUpOptions) => void;
  isSyncAudioPlaying: () => boolean;
}) => {
  readerTextStore = dependencies.readerTextStore;
  readerViewStore = dependencies.readerViewStore;
  updateDictionary = dependencies.updateDictionary;
  setPreviousNewContext = dependencies.setPreviousNewContext;
  readAloudButton = dependencies.readAloudButton;
  showWordPopUp = dependencies.showWordPopUp;
  isSyncAudioPlaying = dependencies.isSyncAudioPlaying;
};

export const resetUpsellFlag = () => {
  shownUpsell = false;
};

const showPhraseHighlightLimitUpsell = (innerOptions) => {
  if (shownUpsell) {
    return;
  }
  shownUpsell = true;
  premiumUpsellModal.show({
    title: "phraseHighlightLimit",
    allPhrasesLimit: true,
    heading: "Upgrade to remove limit of 200 highlights",
    subheading: innerOptions.count
      ? "Aid your recall by seeing each of the " +
        innerOptions.count +
        " words and phrases you are learning highlighted in each text you read..."
      : undefined,
    showTitle: false,
    onCancel: function () {
      stores.user.save({ readerWordHighlights: "off" });
    },
  });
  analytics.trackEvent("phraseHighlightLimit");
};

const publishYourPhrases = () => {
  readerTextStore.yourPhrases.value = new Map(yourPhrasesByPosition);
};

const addYourPhraseAtPosition = (position: number, phrase: string) => {
  const phrases = _.union(yourPhrasesByPosition.get(position) ?? [], [phrase]);
  yourPhrasesByPosition.set(position, phrases);
};

const removeYourPhraseAtPosition = (position: number, phrase: string) => {
  const phrases = _.without(yourPhrasesByPosition.get(position) ?? [], phrase);
  if (phrases.length === 0) {
    yourPhrasesByPosition.delete(position);
  } else {
    yourPhrasesByPosition.set(position, phrases);
  }
};

const buildTokenIndex = (
  parsed: ParsedReaderText,
  requireAdjacency: boolean
): TokenIndex => {
  const index = { ...emptyTokenIndex(), requireAdjacency };
  let previousWordItem: ReaderTextItem | undefined;
  for (const item of parsed.items) {
    if (item.token.kind !== "word") {
      continue;
    }
    const text = item.token.text.toLowerCase();
    const position = item.token.position;
    const textPositions = index.textToPosition.get(text);
    if (textPositions) {
      textPositions.push(position);
    } else {
      index.textToPosition.set(text, [position]);
    }
    index.positionToText[position] = text;
    if (
      previousWordItem &&
      previousWordItem.token.kind === "word" &&
      previousWordItem.token.position === position - 1 &&
      previousWordItem.index === item.index - 1 &&
      previousWordItem.blockIndex === item.blockIndex
    ) {
      index.adjacentPositions.add(position - 1);
    }
    previousWordItem = item;
  }
  return index;
};

/** Index the page's words by text, for phrase lookups. */
export const indexTokens = () => {
  yourPhrasesByPosition = new Map();

  const parsed = readerTextStore.tokens.value;
  tokenIndex = parsed
    ? buildTokenIndex(
        parsed,
        Boolean(
          languageMap.map[stores.bookModel.book.value.language]
            ?.noWordBoundaries
        )
      )
    : emptyTokenIndex();
};

const phraseMatchesAtPosition = (
  index: TokenIndex,
  startPosition: number,
  wordList: string[]
): boolean =>
  wordList.every((word, wordIndex) => {
    const position = startPosition + wordIndex;
    return (
      index.positionToText[position] === word &&
      (wordIndex === 0 ||
        !index.requireAdjacency ||
        index.adjacentPositions.has(position - 1))
    );
  });

const findPhrasePositions = (
  index: TokenIndex,
  wordList: string[]
): number[] => {
  let matches: number[] = [];

  for (const position of index.textToPosition.get(wordList[0]) ?? []) {
    if (phraseMatchesAtPosition(index, position, wordList)) {
      matches = matches.concat(_.range(position, position + wordList.length));
    }
  }

  return matches;
};

/**
 * Pure core of the initial highlight pass: for every word position, every
 * saved phrase (keyed by first token) whose tokens all match starting there.
 * Overlapping saved phrases (e.g. 中国 and 中国人) each get recorded.
 */
const collectPhraseHighlights = (
  index: TokenIndex,
  phraseIndex: PhraseIndex
): Map<number, string[]> => {
  const phrasesByPosition = new Map<number, string[]>();
  for (const positionKey of Object.keys(index.positionToText)) {
    const position = parseInt(positionKey, 10);
    const text = index.positionToText[position];
    // phraseIndex is a plain object, so a word like "constructor" can hit an
    // Object.prototype property instead of a saved-phrase list.
    const phrases = phraseIndex[text];
    if (!Array.isArray(phrases)) {
      continue;
    }
    for (const phrase of phrases) {
      if (
        typeof phrase !== "object" ||
        !phraseMatchesAtPosition(index, position, phrase.split)
      ) {
        continue;
      }
      for (const [wordIndex] of phrase.split.entries()) {
        const wordPosition = position + wordIndex;
        phrasesByPosition.set(
          wordPosition,
          _.union(phrasesByPosition.get(wordPosition) ?? [], [phrase.whole])
        );
      }
    }
  }
  return phrasesByPosition;
};

const addHighlightedPhrase = (data) => {
  if (data.language !== stores.bookModel.book.value.language) {
    console.error("wrong language");
    return;
  }

  for (const position of findPhrasePositions(tokenIndex, data.split)) {
    addYourPhraseAtPosition(position, data.whole);
  }
  publishYourPhrases();
};

const removeHighlightedPhrase = (data) => {
  if (data.language !== stores.bookModel.book.value.language) {
    console.error("wrong language");
    return;
  }

  for (const position of findPhrasePositions(tokenIndex, data.split)) {
    removeYourPhraseAtPosition(position, data.whole);
  }
  publishYourPhrases();
};

// highlight user's phrases
const highlightAllPhrases = async (innerOptions) => {
  let phraseIndex;
  try {
    phraseIndex = await stores.allPhrasesIndex.fetch(
      _.defaults({}, innerOptions, {
        language: stores.bookModel.book.value.language,
      })
    );
  } catch (err) {
    if (err && err.message === "allPhrasesLimit") {
      showPhraseHighlightLimitUpsell(err);
      return;
    }
  }

  for (const [position, phrases] of collectPhraseHighlights(
    tokenIndex,
    phraseIndex ?? {}
  )) {
    for (const phrase of phrases) {
      addYourPhraseAtPosition(position, phrase);
    }
  }
  publishYourPhrases();

  if (!highlightPhrasesInitialized) {
    highlightPhrasesInitialized = true;
    stores.allPhrasesIndex.on("add", addHighlightedPhrase);
    stores.allPhrasesIndex.on("remove", removeHighlightedPhrase);
  }

  readerTextStore.setYourWordClickHandler((position, element, context) => {
    const phrases = yourPhrasesByPosition.get(position) ?? [];

    const newContext = {
      text: context,
      bookID: stores.bookModel.id,
    };

    showWordPopUp({
      targetElement: $(element),
      containerElement: $(".container"),
      phrases: phrases,
      remoteUserWordsFactory: stores.remoteUserWordsFactory,
      userModel: stores.user,
      readerViewStore: readerViewStore,
      newContext,
      fetchedWordCallback: (userWord) => {
        updateDictionary({ userWord, newContext, position });
        setPreviousNewContext(newContext);
      },
    });

    if (
      !readAloudButton?.isPlaying &&
      stores.user.speakingModeEnabled(stores.bookModel.book.value.language) &&
      !isSyncAudioPlaying()
    ) {
      // play longest phrase
      let longestPhrase = "";
      _.each(phrases, (phrase) => {
        if (phrase.length > longestPhrase.length) {
          longestPhrase = phrase;
        }
      });
      tts.play({
        user: stores.user,
        feature: "speakingMode",
        language: stores.bookModel.book.value.language,
        text: longestPhrase,
      });
    }
  });
};

export const disableHighlightAllPhrases = () => {
  if (highlightPhrasesInitialized) {
    stores.allPhrasesIndex.off("add", addHighlightedPhrase);
    stores.allPhrasesIndex.off("remove", removeHighlightedPhrase);
    highlightPhrasesInitialized = false;
  }
  yourPhrasesByPosition = new Map();
  if (readerTextStore) {
    publishYourPhrases();
  }
};

export const updateAutoHighlights = () => {
  if (stores.user.state.value.readerWordHighlights === "on") {
    highlightAllPhrases({});
  } else if (stores.user.state.value.readerWordHighlights === "readyToLearn") {
    highlightAllPhrases({ filter: "readyToLearn" });
  } else {
    disableHighlightAllPhrases();
  }
};

export const _functionsForUnitTests = {
  buildTokenIndex,
  phraseMatchesAtPosition,
  findPhrasePositions,
  collectPhraseHighlights,
};
